import { useEffect, useState } from 'react'
import { ChevronRight } from '../../components/ChevronRight'
import { ScreenHeader } from '../../components/ScreenHeader'
import { SectionTitle } from '../../components/SectionTitle'
import { BackgroundTitle } from '../../components/BackgroundTitle'
import type { NavTab } from '../types'
import { useAuth } from '../../lib/auth'
import { useT } from '../../i18n'
import { readError } from '../../lib/errors'
import { supabase } from '../../lib/supabase'
import {
  daysLeftInWeek, fetchGallery, getWeeklyPeriods, type WeeklyPeriods,
} from '../../lib/api/weekly'
import {
  ensureMyColorfulWeeklyEntry,
  getOrCreateColorfulWeekly, thisMondayISO,
  type ColorfulWeeklyEntryRow, type ColorfulWeeklyEventRow,
} from '../../games/colorful/api/weekly'
import {
  buildPromptText,
  daysUntilNextMonday as daysUntilNextMondayThreeStep,
  ensureMyThreeStepWeeklyEntry,
  fetchLastFinalizedThreeStepWeekly,
  getOrCreateThreeStepWeekly,
  type ThreeStepWeeklyEntryRow, type ThreeStepWeeklyEventRow,
} from '../../games/three-step/api/weekly'
import {
  fetchWordGamesToday, type WordGamesToday,
} from '../../lib/api/wordGames'

interface Props {
  onNavigate: (tab: NavTab) => void
  onIETWeekly: () => void
  onColorfulWeekly: () => void
  onThreeStepWeekly: () => void
  onFakeOut: () => void
  onCloseEnough: () => void
  onFakeOutArchive: () => void
  onCloseEnoughArchive: () => void
  onVoteIET: (eventId: string) => void
  onVoteThreeStep: (eventId: string) => void
  onOpenIETArchive: () => void
  onOpenColorfulArchive: () => void
  onOpenThreeStepArchive: () => void
}

/**
 * Weekly tab landing — flat list of cards, one per affordance:
 *   1. Weekly I'd Eat That (deep-link to the IET weekly hub)
 *   2. Weekly Colorful (deep-link to the Colorful weekly hub)
 *   3. Vote on last week's IET (if there are finalized stories)
 *   4. I'd Eat That archive (read past winning stories)
 *   5. Colorful archive (see past crowd picks per subject)
 *
 * Data is fetched here once so the cards can show real counts and
 * countdowns without each card re-doing work. Past-week results
 * live in the per-game archives below so they don't get rediscovered
 * here every week.
 */
export function WeeklyLandingScreen({
  onNavigate,
  onIETWeekly, onColorfulWeekly, onThreeStepWeekly,
  onFakeOut, onCloseEnough,
  onFakeOutArchive, onCloseEnoughArchive,
  onVoteIET, onVoteThreeStep,
  onOpenIETArchive, onOpenColorfulArchive, onOpenThreeStepArchive,
}: Props) {
  const { session, profile, configured } = useAuth()
  const t = useT()
  const signedIn = Boolean(session && profile)

  const [iet, setIet] = useState<{
    periods: WeeklyPeriods | null
    voteCount: number
    writeCount: number
  } | null>(null)
  const [colorful, setColorful] = useState<{
    thisWeek: ColorfulWeeklyEventRow | null
    entry: ColorfulWeeklyEntryRow | null
    /** Community count of "color theorists" — players who've made at least one pick. */
    theoristsCount: number
  } | null>(null)
  const [threeStep, setThreeStep] = useState<{
    thisWeek: ThreeStepWeeklyEventRow | null
    lastWeek: ThreeStepWeeklyEventRow | null
    entry: ThreeStepWeeklyEntryRow | null
    /** Community count of "solutionists" — players who've written at least one step. */
    solutionistsCount: number
  } | null>(null)
  // Daily word games — null per game unless the server says this
  // account may see it (flag live, or admin during the test period).
  const [wordGames, setWordGames] = useState<WordGamesToday | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!configured) { setLoading(false); return }
    let cancelled = false
    async function load() {
      try {
        const weekStart = thisMondayISO()
        const [periods, cThisWeek, tsThisWeek] = await Promise.all([
          getWeeklyPeriods(),
          getOrCreateColorfulWeekly(weekStart),
          getOrCreateThreeStepWeekly(weekStart),
        ])
        if (cancelled) return

        // The three count_* RPCs are SECURITY DEFINER and bypass the
        // entry-table RLS, so we get the true community engagement
        // count without exposing other users' draft content. Without
        // this, RLS forces the count to 1 (just yourself) during the
        // active week and the labels read wrong.
        const [writeGallery, voteGallery, cEntry, tsLast, tsEntry, wg, cTheorists, tsSolutionists] = await Promise.all([
          fetchGallery(periods.write.id),
          periods.vote ? fetchGallery(periods.vote.id) : Promise.resolve(null),
          signedIn ? ensureMyColorfulWeeklyEntry(cThisWeek.id).catch(() => null) : Promise.resolve(null),
          fetchLastFinalizedThreeStepWeekly(weekStart).catch(() => null),
          signedIn ? ensureMyThreeStepWeeklyEntry(tsThisWeek.id).catch(() => null) : Promise.resolve(null),
          fetchWordGamesToday().catch(() => null),
          // RPCs not yet in generated database.types — cast through any.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (supabase as any).rpc('count_weekly_colorful_theorists',     { p_event_id: cThisWeek.id  }).then((r: { data: number | null }) => Number(r.data ?? 0), () => 0),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (supabase as any).rpc('count_weekly_three_step_solutionists', { p_event_id: tsThisWeek.id }).then((r: { data: number | null }) => Number(r.data ?? 0), () => 0),
        ])
        if (cancelled) return

        // Match the WeeklyHubScreen's `hasStory` predicate — ensure
        // we don't surface ghost rows from guests who tapped into the
        // hub but never wrote anything. ensure_weekly_submission
        // upserts an empty placeholder for every visit, which the
        // raw .length over-counts as "real" submissions.
        const hasStory = (s: { title: string; course1_text: string }) =>
          s.title.trim().length > 0 || s.course1_text.trim().length > 10
        setIet({
          periods,
          writeCount: writeGallery.filter(hasStory).length,
          voteCount: voteGallery?.filter(hasStory).length ?? 0,
        })
        setColorful({
          thisWeek: cThisWeek,
          entry: cEntry,
          theoristsCount: cTheorists,
        })
        setThreeStep({
          thisWeek: tsThisWeek,
          lastWeek: tsLast,
          entry: tsEntry,
          solutionistsCount: tsSolutionists,
        })
        setWordGames(wg)
      } catch (e) {
        if (!cancelled) setError(readError(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [configured, signedIn, profile?.id])

  return (
    <div className="screen pb-24">
      <BackgroundTitle text="Weekly" color="#D9E026" />
      <ScreenHeader
        title={t('WEEKLY')}
        titleColorClass="text-brand-lime-dark"
        profile={profile}
        onProfileTap={() => onNavigate('profile')}
      />

      <div className="px-5 space-y-3 overflow-y-auto pb-6 pt-3 relative z-10">
        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30">
            <p className="text-sm text-brand-danger">{error}</p>
          </div>
        )}

        {loading ? (
          <>
            <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
            <div className="h-16 rounded-2xl bg-brand-elevated animate-pulse" />
          </>
        ) : (
          <>
            {/* Daily word games — cards only render when the server
                says this account may see them. */}
            {wordGames?.fake_out?.available && (
              <button
                onClick={onFakeOut}
                className="w-full bg-game-fakeout-light border-2 border-game-fakeout-dark
                           rounded-2xl shadow-card p-4 flex items-center gap-4 text-left
                           active:scale-[0.98] transition-transform"
              >
                <img src="/games/fake-out.webp" alt="FAKE OUT"
                     className="w-16 h-16 rounded-2xl object-cover shrink-0 shadow-xs" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-game-fakeout-dark">
                      {t('Daily · Spot the real definition')}
                    </p>
                    <span className="text-[9px] font-semibold tracking-widest whitespace-nowrap
                                     shrink-0 rounded-full border px-2 py-0.5
                                     border-game-fakeout-dark/50 text-game-fakeout-dark">
                      {t('FREE TO PLAY')}
                    </span>
                  </div>
                  <h3 className="font-display text-lg font-bold text-brand-text">
                    {t('WEEKLY {game}', { game: 'FAKE OUT' })}
                  </h3>
                  <p className="text-xs text-brand-muted mt-0.5">
                    {wordGames.fake_out.entered
                      ? t('Played · {pts} pts today · {week} this week', { pts: wordGames.fake_out.score, week: wordGames.fake_out.week_total })
                      : wordGames.fake_out.week_total > 0
                        ? t('New words are in · {week} this week', { week: wordGames.fake_out.week_total })
                        : t('New words are in')}
                  </p>
                </div>
                <ChevronRight size={20} color="#6B8A34" className="shrink-0" />
              </button>
            )}

            {wordGames?.close_enough?.available && (
              <button
                onClick={onCloseEnough}
                className="w-full bg-game-closeenough-light border-2 border-game-closeenough
                           rounded-2xl shadow-card p-4 flex items-center gap-4 text-left
                           active:scale-[0.98] transition-transform"
              >
                <img src="/games/close-enough.webp" alt="CLOSE ENOUGH"
                     className="w-16 h-16 rounded-2xl object-cover shrink-0 shadow-xs" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-game-closeenough-dark">
                      {t('Daily · Hunt the secret word')}
                    </p>
                    <span className="text-[9px] font-semibold tracking-widest whitespace-nowrap
                                     shrink-0 rounded-full border px-2 py-0.5
                                     border-game-closeenough text-game-closeenough-dark">
                      {t('FREE TO PLAY')}
                    </span>
                  </div>
                  <h3 className="font-display text-lg font-bold text-brand-text">
                    {t('WEEKLY {game}', { game: 'CLOSE ENOUGH' })}
                  </h3>
                  <p className="text-xs text-brand-muted mt-0.5">
                    {wordGames.close_enough.solved
                      ? t('Solved · {pts} pts today · {week} this week', { pts: wordGames.close_enough.score, week: wordGames.close_enough.week_total })
                      : wordGames.close_enough.guesses > 0
                        ? t('{n} guesses in', { n: wordGames.close_enough.guesses })
                        : t('A secret word is hiding')}
                  </p>
                </div>
                <ChevronRight size={20} color="#A8A020" className="shrink-0" />
              </button>
            )}

            {/* Weekly I'd Eat That — IET-flavoured card per house rule. */}
            {iet?.periods && (
              <button
                onClick={onIETWeekly}
                className="w-full iet-gold border-2 border-game-iet-dark
                           rounded-2xl shadow-card p-4 flex items-center gap-4 text-left
                           active:scale-[0.98] transition-transform"
              >
                <img src="/games/ideatthat.webp" alt="I'D EAT THAT"
                     className="w-16 h-16 rounded-2xl object-cover shrink-0 shadow-xs" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold text-game-iet-dark uppercase tracking-widest">{t('This week · Write')}</p>
                  <h3 className="font-display text-lg font-bold text-brand-text">
                    {t('WEEKLY {game}', { game: "I'D EAT THAT" })}
                  </h3>
                  <p className="text-xs text-brand-muted mt-0.5">
                    {daysLeftInWeek(iet.periods.write.week_start)}d left · {iet.writeCount} storyteller{iet.writeCount === 1 ? '' : 's'}
                  </p>
                </div>
                <ChevronRight size={20} color="#B58615" className="shrink-0" />
              </button>
            )}

            {/* Weekly Colorful */}
            {colorful?.thisWeek && (
              <button
                onClick={onColorfulWeekly}
                className="w-full bg-game-colorful-light border-2 border-game-colorful-dark
                           rounded-2xl shadow-card p-4 flex items-center gap-4 text-left
                           active:scale-[0.98] transition-transform"
              >
                <img src="/games/colorful.webp" alt="COLORFUL"
                     className="w-16 h-16 rounded-2xl object-cover shrink-0 shadow-xs" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-widest"
                     style={{ color: '#74705F' }}>{t('This week · Pick')}</p>
                  <h3 className="font-display text-lg font-bold text-brand-text">
                    {t('WEEKLY {game}', { game: 'COLORFUL' })}
                  </h3>
                  <p className="text-xs text-brand-muted mt-0.5">
                    {daysLeftInWeek(colorful.thisWeek.week_start)}d left · {colorful.theoristsCount} color theorist{colorful.theoristsCount === 1 ? '' : 's'}
                  </p>
                </div>
                <ChevronRight size={20} color="#74705F" className="shrink-0" />
              </button>
            )}

            {/* Weekly Three Step Solution */}
            {threeStep?.thisWeek && (
              <button
                onClick={onThreeStepWeekly}
                className="w-full bg-game-three-step-light border-2 border-game-three-step-dark
                           rounded-2xl shadow-card p-4 flex items-center gap-4 text-left
                           active:scale-[0.98] transition-transform"
              >
                <img src="/games/three-step.webp" alt="THREE STEP SOLUTION"
                     className="w-16 h-16 rounded-2xl object-cover shrink-0 shadow-xs" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-widest
                                text-game-three-step-dark">{t('This week · Write')}</p>
                  <h3 className="font-display text-lg font-bold text-brand-text">
                    {t('WEEKLY {game}', { game: t('THREE STEP') })}
                  </h3>
                  <p className="text-xs text-brand-muted mt-0.5">
                    {daysUntilNextMondayThreeStep(threeStep.thisWeek.week_start)}d left · {threeStep.solutionistsCount} solutionist{threeStep.solutionistsCount === 1 ? '' : 's'}
                  </p>
                </div>
                <ChevronRight size={20} color="#0EAE93" className="shrink-0" />
              </button>
            )}

            {(threeStep?.lastWeek || (iet?.periods?.vote && iet.voteCount > 0)) && (
              <SectionTitle className="pt-2">{t('Vote')}</SectionTitle>
            )}

            {/* Vote card for last week's Three Step */}
            {threeStep?.lastWeek && (
              <button
                onClick={() => onVoteThreeStep(threeStep.lastWeek!.id)}
                className="w-full bg-white border-2 border-brand-border
                           rounded-2xl shadow-card p-4 flex items-center gap-4 text-left
                           active:scale-[0.98] transition-transform"
              >
                <div className="w-12 h-12 rounded-2xl bg-game-three-step-light
                                flex items-center justify-center shrink-0">
                  <span className="text-2xl">👍</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold text-game-three-step-dark
                                uppercase tracking-widest">{t('Vote on three-step solutions')}</p>
                  <h3 className="font-semibold text-brand-text">
                    LAST WEEK · THREE STEP
                  </h3>
                  <p className="text-xs text-brand-muted truncate">
                    {buildPromptText(threeStep.lastWeek.action_a, threeStep.lastWeek.action_b, t)}
                  </p>
                </div>
                <ChevronRight size={20} color="#0EAE93" className="shrink-0" />
              </button>
            )}

            {/* Vote card — neutral white (no game ownership) so it
                follows the white-card rule: grey stroke + same shadow. */}
            {iet?.periods?.vote && iet.voteCount > 0 && (
              <button
                onClick={() => onVoteIET(iet.periods!.vote!.id)}
                className="w-full bg-white border-2 border-brand-border
                           rounded-2xl shadow-card p-4 flex items-center gap-4 text-left
                           active:scale-[0.98] transition-transform"
              >
                <div className="w-12 h-12 rounded-2xl bg-iet-green/10 flex items-center justify-center shrink-0">
                  <span className="text-2xl">👍</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold text-iet-green uppercase tracking-widest">{t('View stories and vote')}</p>
                  <h3 className="font-semibold text-brand-text">
                    LAST WEEK · I'D EAT THAT
                  </h3>
                  <p className="text-xs text-brand-muted">
                    {iet.voteCount} stor{iet.voteCount === 1 ? 'y' : 'ies'} to read
                  </p>
                </div>
                <ChevronRight size={20} color="#6B7280" className="shrink-0" />
              </button>
            )}

            <SectionTitle className="pt-2">{t('Archive')}</SectionTitle>

            {/* IET archive — read past winning stories */}
            <button
              onClick={onOpenIETArchive}
              className="w-full iet-gold border-2 border-game-iet-dark
                         rounded-2xl shadow-card p-4 flex items-center gap-4 text-left
                         active:scale-[0.98] transition-transform"
            >
              <img src="/games/ideatthat.webp" alt="I'D EAT THAT"
                   className="w-12 h-12 rounded-2xl object-cover shadow-xs shrink-0 opacity-90" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold text-game-iet-dark uppercase tracking-widest">
                  Archive
                </p>
                <h3 className="font-semibold text-brand-text">I'D EAT THAT</h3>
                <p className="text-xs text-brand-muted">{t('Read winning stories from every past week')}</p>
              </div>
              <ChevronRight size={20} color="#B58615" className="shrink-0" />
            </button>

            {/* Colorful archive — see past crowd picks per subject */}
            <button
              onClick={onOpenColorfulArchive}
              className="w-full bg-game-colorful-light border-2 border-game-colorful-dark
                         rounded-2xl shadow-card p-4 flex items-center gap-4 text-left
                         active:scale-[0.98] transition-transform"
            >
              <img src="/games/colorful.webp" alt="COLORFUL"
                   className="w-12 h-12 rounded-2xl object-cover shadow-xs shrink-0 opacity-90" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-widest"
                   style={{ color: '#74705F' }}>
                  Archive
                </p>
                <h3 className="font-semibold text-brand-text">COLORFUL</h3>
                <p className="text-xs text-brand-muted">{t('See which colors the crowd picked for each subject')}</p>
              </div>
              <ChevronRight size={20} color="#74705F" className="shrink-0" />
            </button>

            {/* Three Step archive — past winning three-step solutions */}
            <button
              onClick={onOpenThreeStepArchive}
              className="w-full bg-game-three-step-light border-2 border-game-three-step-dark
                         rounded-2xl shadow-card p-4 flex items-center gap-4 text-left
                         active:scale-[0.98] transition-transform"
            >
              <img src="/games/three-step.webp" alt="THREE STEP SOLUTION"
                   className="w-12 h-12 rounded-2xl object-cover shadow-xs shrink-0 opacity-90" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-widest
                              text-game-three-step-dark">
                  Archive
                </p>
                <h3 className="font-semibold text-brand-text">THREE STEP SOLUTION</h3>
                <p className="text-xs text-brand-muted">{t('Read the winning three-step answer from every past prompt')}</p>
              </div>
              <ChevronRight size={20} color="#0EAE93" className="shrink-0" />
            </button>

            {/* Word game archives — same gate as the daily cards. */}
            {wordGames?.fake_out && (
              <button
                onClick={onFakeOutArchive}
                className="w-full bg-game-fakeout-light border-2 border-game-fakeout-dark
                           rounded-2xl shadow-card p-4 flex items-center gap-4 text-left
                           active:scale-[0.98] transition-transform"
              >
                <img src="/games/fake-out.webp" alt="FAKE OUT"
                     className="w-12 h-12 rounded-2xl object-cover shadow-xs shrink-0 opacity-90" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-game-fakeout-dark">
                    Archive
                  </p>
                  <h3 className="font-semibold text-brand-text">FAKE OUT</h3>
                  <p className="text-xs text-brand-muted">{t("Every past week's top ten")}</p>
                </div>
                <ChevronRight size={20} color="#6B8A34" className="shrink-0" />
              </button>
            )}

            {wordGames?.close_enough && (
              <button
                onClick={onCloseEnoughArchive}
                className="w-full bg-game-closeenough-light border-2 border-game-closeenough
                           rounded-2xl shadow-card p-4 flex items-center gap-4 text-left
                           active:scale-[0.98] transition-transform"
              >
                <img src="/games/close-enough.webp" alt="CLOSE ENOUGH"
                     className="w-12 h-12 rounded-2xl object-cover shadow-xs shrink-0 opacity-90" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-game-closeenough-dark">
                    Archive
                  </p>
                  <h3 className="font-semibold text-brand-text">CLOSE ENOUGH</h3>
                  <p className="text-xs text-brand-muted">{t("Every past week's top ten")}</p>
                </div>
                <ChevronRight size={20} color="#A8A020" className="shrink-0" />
              </button>
            )}
          </>
        )}
      </div>

    </div>
  )
}

